import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Typography from '@src/new-components/Typography';
import Tooltip from '@src/components/Tooltip';
import { COLOR } from '@src/styles/color';
import { StyledMoreLanguages } from './index.style';

// How many language names are spelled out before the rest collapse into a
// hoverable "and N other languages". Today the transcript models whitelist only
// two languages, so the overflow branch is dormant — it exists so adding a third
// (or thirteenth) language to the feature flag needs no code change.
const VISIBLE_COUNT = 3;

// `Trans` clones the mapped component and overwrites its children with the
// translated text, so the tooltip and its hover target have to live in one
// element — nesting them in the `components` array would drop the inner span
// and leave MUI a bare string it cannot attach a ref to.
const MoreLanguages = ({ names, children }) => (
  <Tooltip title={names} placement="top" arrow>
    <StyledMoreLanguages>{children}</StyledMoreLanguages>
  </Tooltip>
);

/**
 * Caption under the language dropdown naming the languages the selected model
 * accepts. Renders nothing when `labels` is empty — the caller decides whether
 * the model has a finite whitelist at all.
 *
 * @param {string[]} labels — localized language names, already ordered as in the dropdown.
 */
const SupportedLanguagesNote = ({ labels = [] }) => {
  const { t } = useTranslation();

  if (!labels.length) return null;

  const visible = labels.slice(0, VISIBLE_COUNT);
  const hidden = labels.slice(VISIBLE_COUNT);
  const languages = visible.join(', ');

  return (
    <Typography variant="body-12-regular" color={COLOR.label2[64]}>
      {hidden.length === 0 ? (
        t('onlySupportLanguages', { languages })
      ) : (
        // `Trans` keeps the hoverable segment inside the sentence, so each
        // locale can place it wherever its grammar needs.
        <Trans
          t={t}
          i18nKey="onlySupportLanguagesMore"
          values={{ languages, value: hidden.length }}
          components={[<MoreLanguages names={hidden.join(', ')} />]}
        />
      )}
    </Typography>
  );
};

export default SupportedLanguagesNote;
